import React, {useEffect} from 'react';
import {Sheet, SheetContent, SheetTrigger} from "../ui/sheet";
import {Button} from "../ui/button";
import {Textarea} from "../ui/textarea";
import {ScrollArea} from "../ui/scroll-area";
import {cn} from "../ui/lib/utils";
import {Bot, ChevronDown, ChevronRight, Clock, Menu, Plus, Send, Terminal, User, Image, X, Camera} from "lucide-react";
import Markdown from 'react-markdown';
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";
import {LoadingSpinner} from "../ui/customSpinner";
import useAuth from "hooks/useAuth";
import CustomAxios from "../../utility/customAxios";
import AnimatedLogo from "../ui/AnimatedLogo";
import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "../ui/collapsible";
import {useFeatureFlagsStore} from "../../store/featureFlagsStore";
import { toolRenderers, ToolRendererProps, DefaultToolCallRenderer, DefaultToolResponseRenderer, ToolRendererComponent } from "./ToolRenderers";
import { m } from 'framer-motion';
import {MarkdownRenderer} from "./MarkdownRenderer";
import {useSearchParams} from "react-router-dom";

export interface Message {
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
    screenshot?: string; // Base64 encoded screenshot
}

interface ChatHistory {
    id: string;
    title: string;
    createdAt: Date;
}

interface ToolMessage {
    role: 'tool';
    content: string;
    tool_name?: string;
    tool_call?: boolean;
    tool_call_id?: string;
    tool_call_function_name?: string;
    tool_call_function_args?: string;
    tool_call_response_id?: string;
    tool_call_response_content?: string;
}

interface ToolCall {
    id: string;
    name: string;
    arguments: string;
}

interface StreamResponse {
    content: string;
    tool_calls?: ToolCall[];
    is_complete: boolean;
}

function isToolMessage(message: Message): message is ToolMessage {
    return message.role === 'tool';
}

function TypingIndicator() {
    return (
        <div className="flex space-x-2 p-1">
            <div className="w-2 h-2 rounded-full bg-primary/40 animate-bounce [animation-delay:-0.3s]"></div>
            <div className="w-2 h-2 rounded-full bg-primary/40 animate-bounce [animation-delay:-0.15s]"></div>
            <div className="w-2 h-2 rounded-full bg-primary/40 animate-bounce"></div>
        </div>
    );
}

function wouldRenderToolMessage(message: Message): boolean {
    // Check if there's a custom renderer for this tool
    const toolName = message.tool_name;
    const CustomRenderer = toolName && toolRenderers[toolName] as ToolRendererComponent;
    
    // If the custom renderer has a shouldRender method, use it
    if (CustomRenderer && typeof CustomRenderer.shouldRender === 'function') {
        return CustomRenderer.shouldRender(message);
    }
    
    // By default, render all tool messages
    return true;
}

function ToolMessageRenderer({ message, isToolCall }: { message: Message, isToolCall: boolean }) {
    // Check if there's a custom renderer for this tool
    const toolName = isToolCall ? message.tool_call_function_name : message.tool_name;
    const CustomRenderer = toolName && toolRenderers[toolName];
    
    // If we have a custom renderer for this tool, use it
    if (CustomRenderer) {
        return <CustomRenderer message={message} isToolCall={isToolCall} />;
    }
    
    // Otherwise use the default renderers from ToolRenderers.tsx
    if (message.tool_call_function_name) {
        return <DefaultToolCallRenderer message={message} />;
    } else {
        return <DefaultToolResponseRenderer message={message} />;
    }
}

function ChatHistorySlider({ 
    isOpen, 
    onClose, 
    histories,
    activeId,
    onSelect,
    onNewChat
}: { 
    isOpen: boolean; 
    onClose: () => void;
    histories: ChatHistory[];
    activeId: string | null;
    onSelect: (history: ChatHistory) => void;
    onNewChat: () => void;
}) {
    return (
        <div 
            className={cn(
                "absolute inset-y-0 w-[260px] bg-[#0A0F1E] border-r border-[#1D2739] transition-all duration-300 ease-in-out",
                isOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
            )}
        >
            <div className="flex flex-col h-full">
                <div className="flex items-center h-12 px-4 border-b border-[#1D2739]">
                    <span className="text-sm text-textmedium">Chat History</span>
                </div>
                <div className="p-2">
                    <Button
                        onClick={onNewChat}
                        className="w-full bg-primarytransparent hover:bg-primary/90 text-textmedium border border-primary h-9 text-sm font-normal"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        New Chat
                    </Button>
                </div>
                <ScrollArea className={"relative"}>
                    <div className="py-2 space-y-1">
                        {histories.map((history) => (
                            <Button
                                key={history.id}
                                variant="ghost"
                                className={cn(
                                    "w-full justify-start px-4 py-2.5 hover:bg-[#1D2739] text-left h-auto rounded-sm",
                                    activeId === history.id ? "bg-[#1D2739]" : "bg-transparent",
                                    "group transition-colors"
                                )}
                                onClick={() => onSelect(history)}
                            >
                                <div className="flex items-center gap-3 w-full">
                                    <Clock className="h-4 w-4 text-textdark group-hover:text-textmedium flex-shrink-0" />
                                    <div className="flex flex-col min-w-0">
                                        <span className="text-sm text-textmedium group-hover:text-textlight truncate font-medium">
                                            {history.title}
                                        </span>
                                        <span className="text-xs text-textdark group-hover:text-textmedium">
                                            {history.createdAt.toLocaleDateString()}
                                        </span>
                                    </div>
                                </div>
                            </Button>
                        ))}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}

// Guardian sheet and chat button should have high z-index
const getHighZIndexClass = () => {
    // Define CSS in the document header
    if (typeof document !== 'undefined') {
        const styleId = 'guardian-high-z-index-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .guardian-sheet-content {
                    z-index: 9999 !important;
                    transition: width 0.3s ease, max-width 0.3s ease, height 0.3s ease, inset 0.3s ease, transform 0.3s ease !important;
                }
                .guardian-button {
                    z-index: 9998 !important;
                }
                /* Also target all Dialog portals */
                [data-radix-portal] {
                    z-index: 9999 !important;
                }
                /* Custom transition for enhanced mode */
                .guardian-enhanced-transition {
                    transform: none !important;
                    animation: none !important;
                    right: 0 !important;
                    left: auto !important;
                    position: fixed !important;
                    data-[state=open]:animate-none !important;
                    data-[state=closed]:animate-none !important;
                }
                /* Right-aligned normal mode */
                .guardian-normal-mode {
                    right: 0 !important;
                    left: auto !important;
                    transform-origin: right center !important;
                }
                /* Override Radix UI animations */
                .guardian-sheet-content[data-state="open"] {
                    animation: none !important;
                }
                /* Prevent any animation from playing during transition */
                .guardian-sheet-content.transitioning {
                    animation: none !important;
                    transition-timing-function: ease !important;
                }
            `;
            document.head.appendChild(style);
        }
    }
    return 'guardian-sheet-content';
};

// Extract the message component to prevent re-renders
const ChatMessage = React.memo(function ChatMessage({ 
    message, 
    index, 
    isEnhancedMode, 
    totalWindowWidth,
    user,
    prevToolName
}: { 
    message: Message; 
    index: number; 
    isEnhancedMode: boolean;
    totalWindowWidth: number;
    user: any;
    prevToolName: string | undefined;
}) {
    // Create the enhanced message with tool_name properly set
    const enhancedMessage = {
        ...message,
        // Make sure tool_name is set for consistency
        tool_name: message.tool_name || message.tool_call_function_name || prevToolName
    };
    
    // Check if this tool message should be rendered
    if (enhancedMessage.tool_name &&
        !wouldRenderToolMessage(enhancedMessage)) {
        return null;
    }

    return (
        <div
            className={cn(
                "flex w-full",
                message.role === 'user' ? 'bg-transparent py-8 px-6' : 'bg-[#131B2E]',
                message.role === 'tool' ? 'py-2 px-6' : 'py-6 px-6'
            )}
        >
            <div
                style={{
                    maxWidth: isEnhancedMode ? totalWindowWidth : 720
                }}
                className={cn(
                    "flex gap-4 mx-auto w-full",
                )}>
                <div className="flex-shrink-0 w-8 h-8">
                    <div
                        className={cn(
                            "w-8 h-8 rounded flex items-center justify-center",
                            message.role === 'user'
                                ? 'bg-primary text-white'
                                : message.role === 'tool'
                                    ? 'bg-yellow-500/10'
                                    : 'bg-primary/10'
                        )}
                    >
                        {message.role === 'user' ? (
                            <User className="h-5 w-5"/>
                        ) : message.role === 'tool' ? (
                            <Terminal className="h-5 w-5 text-yellow-500"/>
                        ) : (
                            <Bot className="h-5 w-5 text-primary"/>
                        )}
                    </div>
                </div>
                <div className="flex flex-col gap-1 min-h-[20px] w-full">
                    <span className="font-medium text-base text-textlight">
                        {message.role === 'user'
                            ? user?.email || 'User'
                            : message.role === 'tool'
                                ? 'Tool Response'
                                : 'Guardian'
                        }
                    </span>
                    <div className="text-sm text-textmedium space-y-4">
                        {/* Handle tool calls and responses with our custom renderer */}
                        {message.role === 'tool' || message.tool_call_function_name ? (
                            <ToolMessageRenderer 
                                message={enhancedMessage} 
                                isToolCall={message.tool_call || false} 
                            />
                        ) : (
                            <>
                                {message.content && (
                                    <MarkdownRenderer content={message.content} />
                                )}
                                {message.screenshot && (
                                    <div className="mt-3 mb-4">
                                        <div className="border border-[#1D2739] rounded-md p-1 bg-black/20 inline-block">
                                            <img 
                                                src={message.screenshot.startsWith('data:') 
                                                    ? message.screenshot 
                                                    : `data:image/jpeg;base64,${message.screenshot}`
                                                } 
                                                alt="Screenshot" 
                                                className="max-w-full rounded-sm shadow-md"
                                            />
                                        </div>
                                    </div>
                                )}
                                {message.tool_call_function_name && (
                                    <div className="mt-1 flex items-start gap-2">
                                        <div className="flex-1">
                                            {message.tool_call_function_args && (
                                                <pre className="bg-black/20 p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                                                    {message.tool_call_function_name}: {message.tool_call_function_args}
                                                </pre>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

// Extract the input component to prevent re-renders
const ChatInput = React.memo(function ChatInput({
    input,
    setInput,
    isLoading,
    isStopping,
    handleStopGenerating,
    handleSubmit,
    handleScreenshotUpload,
    removeScreenshot,
    screenshot,
    captureAndOpenChat,
    isEnhancedMode,
    totalWindowWidth,
    inputRef,
    handleDragEnter,
    handleDragOver,
    handleDrop
}: {
    input: string;
    setInput: (input: string) => void;
    isLoading: boolean;
    isStopping: boolean;
    handleStopGenerating: () => void;
    handleSubmit: (e: React.FormEvent) => void;
    handleScreenshotUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
    removeScreenshot: () => void;
    screenshot: string | null;
    captureAndOpenChat: () => void;
    isEnhancedMode: boolean;
    totalWindowWidth: number;
    inputRef: React.RefObject<HTMLTextAreaElement>;
    handleDragEnter: (e: React.DragEvent) => void;
    handleDragOver: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
}) {
    return (
        <div className="px-6 pb-6 bg-[#131B2E]">
            <div
                style={{
                    maxWidth: isEnhancedMode ? totalWindowWidth : 740
                }}
                className={cn(
                    "relative mx-auto",
                )}>
                {/* Stop button - only show when loading */}
                {isLoading && (
                    <div className="absolute -top-10 left-0 right-0 flex justify-center">
                        <Button
                            onClick={handleStopGenerating}
                            className="bg-red-500/10 hover:text-textlight text-textmedium border border-red-500 py-1 h-8 px-2 rounded"
                            disabled={isStopping}
                        >
                            {isStopping ? 'Stopping...' : 'Stop ⌘⌫'}
                        </Button>
                    </div>
                )}
                <form 
                    onSubmit={handleSubmit}
                    className="relative flex flex-col w-full bg-[#131B2E] rounded-xl shadow-[0_0_15px_rgba(0,0,0,0.1)] border border-[#1D2739] overflow-hidden"
                    onDragEnter={handleDragEnter}
                >
                    {/* Screenshot preview */}
                    {screenshot && (
                        <div className="px-4 pt-4 pb-2">
                            <div className="border-2 border-primary rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-3 py-2 bg-primary/10">
                                    <span className="text-sm font-medium text-primary">Screenshot attached</span>
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-7 w-7 p-0 hover:bg-primary/20"
                                        onClick={removeScreenshot}
                                    >
                                        <X className="h-5 w-5 text-primary" />
                                    </Button>
                                </div>
                                <div className="flex justify-center p-2 bg-black/20">
                                    <img 
                                        src={screenshot} 
                                        alt="Screenshot preview" 
                                        className="max-h-32 w-auto max-w-full object-contain rounded" 
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Input area */}
                    <div 
                        className="flex bg-backgroundmedium"
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                    >
                        <div className="flex-1">
                            <Textarea
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask Guardian anything..."
                                className="min-h-[60px] max-h-[200px] w-full bg-transparent border-0 resize-none py-4 pl-4 pr-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 text-textmedium placeholder:text-textmedium/50"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSubmit(e);
                                    }
                                }}
                                disabled={isLoading}
                            />
                        </div>
                        <div className="flex items-stretch">
                            {/* Capture screenshot button */}
                            <Button
                                type="button"
                                className="h-auto px-3 rounded-none bg-transparent hover:bg-[#1D2739] disabled:opacity-50 transition-colors border-r border-[#1D2739]"
                                onClick={captureAndOpenChat}
                                disabled={isLoading}
                                title="Capture current screen (⌘G or Ctrl+G)"
                            >
                                <Camera className="h-5 w-5 text-textmedium hover:text-textlight transition-colors" />
                            </Button>
                            
                            {/* Upload screenshot button */}
                            <label className="flex items-center justify-center px-3 cursor-pointer hover:bg-[#1D2739] transition-colors border-r border-[#1D2739] group"
                                   title="Attach screenshot">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleScreenshotUpload}
                                    disabled={isLoading}
                                />
                                <Image className="h-5 w-5 text-textmedium group-hover:text-textlight transition-colors" />
                            </label>
                            <div className="border-l border-[#1D2739] flex justify-center">
                                <Button
                                    type="submit"
                                    className="h-auto px-4 rounded-none bg-transparent hover:bg-[#1D2739] disabled:opacity-50 transition-colors"
                                    disabled={(!input.trim() && !screenshot) || isLoading}
                                >
                                    {isLoading ? (
                                        <LoadingSpinner className="h-4 w-4" />
                                    ) : (
                                        <Send className="h-4 w-4 text-textmedium" />
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
});

export function GuardianChat() {
    const { user } = useAuth();
    const [isOpen, setIsOpen] = React.useState(false);
    const [messages, setMessages] = React.useState<Message[]>([]);
    const [input, setInput] = React.useState('');
    const [isLoading, setIsLoading] = React.useState(false);
    const [screenshot, setScreenshot] = React.useState<string | null>(null);
    const [isDragging, setIsDragging] = React.useState(false);
    const [isHistoryOpen, setIsHistoryOpen] = React.useState(false);
    const [histories, setHistories] = React.useState<ChatHistory[]>([]);
    const [activeHistoryId, setActiveHistoryId] = React.useState<string | null>(null);
    const [isStopping, setIsStopping] = React.useState(false);
    const [isEnhancedMode, setIsEnhancedMode] = React.useState(false);
    const [isTransitioning, setIsTransitioning] = React.useState(false);
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const [isCapturingScreenshot, setIsCapturingScreenshot] = React.useState(false);
    const [isEnabled, setIsEnabled] = React.useState(false);
    // Add a reference to the abort controller for the current SSE connection
    const abortControllerRef = React.useRef<AbortController | null>(null);
    // Add reference to scroll container and track if user is at bottom
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);
    const [isNearBottom, setIsNearBottom] = React.useState(true);
    const [searchParams, setSearchParams] = useSearchParams();

    const featureFlags = useFeatureFlagsStore()

    // Add a ref for the textarea to focus it
    const inputRef = React.useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        featureFlags.featureFlagEnabled("guardian.chat").then((x) => {
            setIsEnabled(x)
        })
    }, []);


    // Global keyboard shortcut for taking a screenshot and stopping generation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Check if Command+G (Mac) or Ctrl+G (Windows/Linux) is pressed
            if ((e.metaKey || e.ctrlKey) && e.key === 'g') {
                e.preventDefault(); // Prevent default browser behavior
                
                // Only proceed if the feature is enabled
                if (isEnabled && !isCapturingScreenshot) {
                    captureAndOpenChat();
                }
            }
            
            // Check if Command+Delete (Mac) or Ctrl+Delete (Windows/Linux) is pressed to stop generation
            if ((e.metaKey || e.ctrlKey) && (e.key === 'Delete' || e.key === 'Backspace')) {
                e.preventDefault(); // Prevent default browser behavior
                
                // Only proceed if the chat is open and generating a response
                if (isEnabled && isOpen && isLoading && !isStopping) {
                    handleStopGenerating();
                }
            }

            // Check if Command+E (Mac) or Ctrl+E (Windows/Linux) is pressed to toggle enhanced mode
            if ((e.metaKey || e.ctrlKey) && e.key === 'e') {
                e.preventDefault(); // Prevent default browser behavior
                
                // Only proceed if the chat is open
                if (isEnabled && isOpen && !isTransitioning) {
                    toggleEnhancedMode();
                }
            }
        };
        
        // Add event listener for keyboard shortcuts
        window.addEventListener('keydown', handleKeyDown);
        
        // Cleanup
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isEnabled, isCapturingScreenshot, isOpen, isLoading, isStopping, isEnhancedMode, isTransitioning]);
    
    // Function to capture screenshot and open chat in one go
    const captureAndOpenChat = async () => {
        // Don't proceed if the feature is disabled or we're already capturing
        if (!isEnabled || isCapturingScreenshot) return;
        
        // Set a flag to indicate we're capturing a screenshot
        setIsCapturingScreenshot(true);
        
        // First close the chat window if it's open to get a clean screenshot
        if (isOpen) {
            setIsOpen(false);
            // Wait for it to close
            await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        try {
            // Use html2canvas to capture the page
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(document.body);
            
            // Convert canvas to base64 image
            const base64Image = canvas.toDataURL('image/png');
            
            // Set the screenshot
            setScreenshot(base64Image);
            
            // Start a new chat if we're currently in an existing chat
            setMessages([]);
            setActiveHistoryId(null);
            setInput('');
            
            // Reset scroll position for new chat
            setIsNearBottom(true);
            
            // Open the chat window
            setIsOpen(true);
            
            // Focus the input field after opening the chat
            // We need a timeout to ensure the sheet is fully opened and rendered
            setTimeout(() => {
                inputRef.current?.focus();
            }, 300);
        } catch (error) {
            console.error('Error capturing screenshot:', error);
            // Open the chat window anyway in case of error
            setIsOpen(true);
        } finally {
            // Reset capturing flag
            setIsCapturingScreenshot(false);
        }
    };

    // Load chat history when component mounts
    React.useEffect(() => {
        const loadHistory = async () => {
            try {
                const { data } = await CustomAxios.get('/api/v1/guardian/history');
                setHistories(data.map((history: any) => ({
                    ...history,
                    createdAt: new Date(history.createdAt)
                })));
            } catch (error) {
                console.error('Error loading chat history:', error);
            }
        };
        loadHistory();
    }, []);

    const scrollToBottom = () => {
        // Only scroll if user is near bottom or this is a user triggered action
        if (isNearBottom) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    };

    // Function to check if scroll position is near bottom
    const checkIfNearBottom = () => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea) return;
        
        const scrollElement = scrollArea.querySelector('[data-radix-scroll-area-viewport]');
        if (!scrollElement) return;
        
        const { scrollHeight, scrollTop, clientHeight } = scrollElement as HTMLElement;
        // Consider "near bottom" if within 100px of the bottom
        const isNear = scrollHeight - scrollTop - clientHeight < 100;
        setIsNearBottom(isNear);
    };

    // Add scroll event listener
    React.useEffect(() => {
        const scrollArea = scrollAreaRef.current;
        if (!scrollArea) return;
        
        const scrollElement = scrollArea.querySelector('[data-radix-scroll-area-viewport]');
        if (!scrollElement) return;
        
        scrollElement.addEventListener('scroll', checkIfNearBottom);
        
        return () => {
            scrollElement.removeEventListener('scroll', checkIfNearBottom);
        };
    }, [isOpen, messages.length]); // Re-attach when chat opens or messages change

    React.useEffect(() => {
        scrollToBottom();
    }, [messages, isNearBottom]);

    // Add new useEffect for scrolling when chat is opened
    React.useEffect(() => {
        if (isOpen) {
            // Add a delay to ensure the sheet is fully opened
            setTimeout(() => {
                setIsNearBottom(true); // Reset to true when opening chat
                scrollToBottom();
                // Focus the input field if it exists
                inputRef.current?.focus();
            }, 300);
        }
    }, [isOpen]);

    // Handle stopping an ongoing chat response
    const handleStopGenerating = () => {
        if (isStopping) return;
        
        setIsStopping(true);
        
        // Abort the SSE connection if it exists
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            
            // The cleanup will happen in the catch block of the fetch
            // We'll mark the response as stopped in the UI immediately
            setMessages(prev => {
                const lastMessage = prev[prev.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                    return [
                        ...prev.slice(0, -1),
                        {
                            ...lastMessage,
                            content: lastMessage.content + "\n\n*Generation stopped by user*"
                        }
                    ];
                }
                return prev;
            });
            
            // Reset states
            setIsLoading(false);
            setIsStopping(false);
        }
    };

    // Handle file selection from the input element
    const handleScreenshotUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        
        // Reset the input so the same file can be selected again
        e.target.value = '';
        
        if (!file) {
            return;
        }
        
        processImageFile(file);
    };

    // Process the image file and set it as the screenshot
    const processImageFile = (file: File) => {
        // Check file size - limit to 5MB
        if (file.size > 5 * 1024 * 1024) {
            alert("Screenshot must be less than 5MB");
            return;
        }
        
        // Check if it's an image file
        if (!file.type.startsWith('image/')) {
            alert("Only image files are supported");
            return;
        }
        
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64 = reader.result as string;
            setScreenshot(base64);
        };
        reader.onerror = (error) => {
            console.error('Error reading file:', error);
        };
        reader.readAsDataURL(file);
    };

    // Handle drag events for any HTML element
    const handleDragEnter = <T extends Element>(e: React.DragEvent<T>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = <T extends Element>(e: React.DragEvent<T>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDragOver = <T extends Element>(e: React.DragEvent<T>) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = <T extends Element>(e: React.DragEvent<T>) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            const file = e.dataTransfer.files[0];
            processImageFile(file);
        }
    };

    const removeScreenshot = () => {
        setScreenshot(null);
    };

    // Memoize handlers
    const handleSubmit = React.useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        if (!input.trim() && !screenshot) return;
        if (isLoading) return;

        // Reset stopping state when sending a new message
        setIsStopping(false);
        
        // Extract base64 data from data URL if it exists
        let screenshotBase64 = null;
        if (screenshot) {
            // Format is data:image/png;base64,DATA
            const base64Data = screenshot.split(',')[1];
            if (base64Data) {
                screenshotBase64 = base64Data;
            }
        }

        const userMessage = { 
            role: 'user' as const, 
            content: input,
            screenshot: screenshotBase64 || undefined // Use undefined instead of null for type compatibility
        };
        const currentInput = input;
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setScreenshot(null);
        setIsLoading(true);
        
        // Force scroll to bottom when user sends a message
        setIsNearBottom(true);
        setTimeout(() => scrollToBottom(), 100);
        
        try {
            const authToken = CustomAxios.defaults.headers.common['Authorization'];
            const location = process.env.REACT_APP_API_URL || window.location.origin;

            // Create a new AbortController for this request
            const abortController = new AbortController();
            abortControllerRef.current = abortController;
            
            // Create fetch request for SSE with the signal from AbortController
            const response = await fetch(location + `/api/v1/guardian/chat/stream`, {
                method: 'POST',
                headers: {
                    'Authorization': authToken as string,
                    'Accept': 'text/event-stream',
                    'Cache-Control': 'no-cache',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    chatId: activeHistoryId || '',
                    message: currentInput,
                    screenshot: screenshotBase64
                }),
                credentials: 'include',
                signal: abortController.signal // Add the abort signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('No reader available');
            }

            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const {value, done} = await reader.read();
                if (done) {
                    break;
                }

                const chunk = decoder.decode(value, {stream: true});
                
                buffer += chunk;
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep the last partial line in the buffer

                for (const line of lines) {
                    if (!line.trim()) {
                        continue;
                    }
                    
                    if (line.startsWith('event:')) {
                        continue;
                    }

                    if (!line.startsWith('data:')) {
                        continue;
                    }

                    const eventData = line.slice(5).trim();

                    try {
                        // Handle chat_id which comes as a plain UUID string
                        if (eventData.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                            setActiveHistoryId(eventData);
                            // Refresh chat history immediately after getting a new chat ID
                            const { data: historyData } = await CustomAxios.get('/api/v1/guardian/history');
                            setHistories(historyData.map((history: any) => ({
                                ...history,
                                createdAt: new Date(history.createdAt)
                            })));
                            continue;
                        }

                        const data = JSON.parse(eventData);
                        
                        if (data.type === 'error') {
                            throw new Error(data.message);
                        }

                        if (data.type === 'stopped') {
                            // Handle the case where the stream was explicitly stopped
                            setIsLoading(false);
                            setIsStopping(false);
                            break;
                        }

                        if (data.type === 'assistant' || data.type === 'tool' || data.type === 'user') {
                            const message = data.message;
                            
                            setMessages(prev => {
                                const newMessages = [...prev];
                                
                                // If it's a user message with a screenshot being returned by the server
                                if (message.role === 'user') {
                                    // Find the last user message
                                    for (let i = newMessages.length - 1; i >= 0; i--) {
                                        if (newMessages[i].role === 'user') {
                                            // Update with server-provided screenshot
                                            if (message.screenshot) {
                                                newMessages[i].screenshot = message.screenshot;
                                            }
                                            break;
                                        }
                                    }
                                    return newMessages;
                                }
                                
                                const lastMessage = newMessages[newMessages.length - 1];
                                
                                if (message.role === 'assistant') {
                                    if (lastMessage?.role === 'assistant') {
                                        // Create a new message object to ensure React detects the change
                                        newMessages[newMessages.length - 1] = {
                                            ...lastMessage,
                                            content: message.content || lastMessage.content,
                                            tool_call_id: message.tool_call_id || lastMessage.tool_call_id,
                                            tool_call_function_name: message.tool_call_function_name || lastMessage.tool_call_function_name,
                                            tool_call_function_args: message.tool_call_function_args || lastMessage.tool_call_function_args,
                                            screenshot: message.screenshot || lastMessage.screenshot,
                                        };
                                    } else {
                                        // Add new assistant message
                                        newMessages.push({
                                            role: 'assistant',
                                            content: message.content || '',
                                            tool_call_id: message.tool_call_id,
                                            tool_call_function_name: message.tool_call_function_name,
                                            tool_call_function_args: message.tool_call_function_args,
                                            screenshot: message.screenshot,
                                        });
                                    }
                                } else if (message.role === 'tool') {
                                    // Parse tool response content if it's JSON
                                    let parsedContent = message.tool_call_response_content;
                                    try {
                                        const parsed = JSON.parse(message.tool_call_response_content || '');
                                        if (Array.isArray(parsed)) {
                                            parsedContent = parsed.join('\n');
                                        } else {
                                            parsedContent = JSON.stringify(parsed, null, 2);
                                        }
                                    } catch (e) {
                                        // If it's not JSON, use as is
                                    }

                                    // Always add tool messages as new messages
                                    newMessages.push({
                                        role: 'tool',
                                        content: message.content || '',
                                        tool_call_response_id: message.tool_call_response_id,
                                        tool_call_response_content: parsedContent,
                                    });
                                }
                                
                                return newMessages;
                            });
                        }

                        if (data.type === 'done') {
                            // Reset the stopping state when done
                            setIsStopping(false);
                            break;
                        }
                    } catch (error) {
                        // Handle the AbortError differently from other errors
                        if (error instanceof DOMException && error.name === 'AbortError') {
                            console.log('SSE connection was intentionally aborted');
                        } else {
                            console.error('Error parsing SSE data:', error);
                            setMessages(prev => [...prev, { 
                                role: 'assistant', 
                                content: 'Sorry, I encountered an error while processing your request.' 
                            }]);
                        }
                    }
                }
            }

            // If this was a new chat, refresh chat history
            if (!activeHistoryId) {
                const { data } = await CustomAxios.get('/api/v1/guardian/history');
                setHistories(data.map((history: any) => ({
                    ...history,
                    createdAt: new Date(history.createdAt)
                })));
            }
        } catch (error) {
            // Handle the AbortError differently from other errors
            if (error instanceof DOMException && error.name === 'AbortError') {
                console.log('SSE connection was intentionally aborted');
            } else {
                console.error('Error:', error);
                setMessages(prev => [...prev, { 
                    role: 'assistant', 
                    content: 'Sorry, I encountered an error while processing your request.' 
                }]);
            }
        } finally {
            // Clean up the abort controller
            abortControllerRef.current = null;
            setIsLoading(false);
        }
    }, [input, screenshot, isLoading, isStopping, activeHistoryId]);

    const handleHistorySelect = React.useCallback(async (history: ChatHistory) => {
        try {
            const { data } = await CustomAxios.get(`/api/v1/guardian/chat?id=${history.id}`);
            // Make sure we process the screenshots properly when loading chat history
            const messagesWithFormattedScreenshots = data.messages.map((msg: any) => ({
                ...msg
                // Screenshot already comes as a base64 string, no need to transform
            }));
            setMessages(messagesWithFormattedScreenshots);
            setActiveHistoryId(history.id);
            setIsHistoryOpen(false);
            
            // Reset scroll position when loading history
            setIsNearBottom(true);
            setTimeout(() => scrollToBottom(), 100);
        } catch (error) {
            console.error('Error loading chat messages:', error);
        }
    }, []);

    // Handle opening Guardian with a specific chat history from URL parameter
    useEffect(() => {
        const guardianChatHistoryIdToOpen = searchParams.get('guardianChatHistoryIdToOpen');
        
        if (guardianChatHistoryIdToOpen && isEnabled && histories.length > 0) {
            // Find the chat history with the specified ID
            const targetHistory = histories.find(history => history.id === guardianChatHistoryIdToOpen);
            
            if (targetHistory) {
                // Open Guardian and load the specified chat history
                setIsOpen(true);
                handleHistorySelect(targetHistory);
                
                // Remove the search parameter from URL
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('guardianChatHistoryIdToOpen');
                setSearchParams(newSearchParams);
            }
        }
    }, [searchParams, isEnabled, histories, handleHistorySelect]);

    const handleNewChat = () => {
        setMessages([]);
        setActiveHistoryId(null);
        setInput('');
        setIsHistoryOpen(false);
        
        // Reset scroll position for new chat
        setIsNearBottom(true);
    };

    // Ensure high z-index styles are added
    React.useEffect(() => {
        getHighZIndexClass();
    }, []);

    // Listen for messages from other components
    React.useEffect(() => {
        const handleGuardianMessage = (event: CustomEvent<{message: string}>) => {
            // Only proceed if the feature is enabled
            if (!isEnabled) return;
            
            // Set the message text content
            setInput(event.detail.message);
            
            // Open the chat window if it's not already open
            if (!isOpen) {
                setIsOpen(true);
                
                // Start a new chat
                setMessages([]);
                setActiveHistoryId(null);
            }
            
            // Focus the input after a short delay to ensure the component is ready
            setTimeout(() => {
                inputRef.current?.focus();
            }, 300);
        };

        // Add event listener for custom messages
        window.addEventListener('metoro:guardian:sendMessage', handleGuardianMessage as EventListener);
        
        // Cleanup
        return () => {
            window.removeEventListener('metoro:guardian:sendMessage', handleGuardianMessage as EventListener);
        };
    }, [isEnabled, isOpen]);

    // Handle the transition between normal and enhanced modes
    const toggleEnhancedMode = () => {
        setIsTransitioning(true);
        // Short delay before changing the actual state to ensure smooth transition
        setTimeout(() => {
            setIsEnhancedMode(prev => !prev);
        }, 10);
    };

    // Reset transitioning state after the transition is complete
    React.useEffect(() => {
        if (isTransitioning) {
            const timer = setTimeout(() => {
                setIsTransitioning(false);
            }, 300); // Match this to your transition duration
            return () => clearTimeout(timer);
        }
    }, [isTransitioning]);

    if (!isEnabled) {
        return
    }

    let totalWindowWidth = window.innerWidth * 0.95;

    return (
        <Sheet open={isOpen} onOpenChange={(open) => {
            // Only allow changing if we're not in the middle of capturing a screenshot
            if (!isCapturingScreenshot) {
                setIsOpen(open);
                // Reset enhanced mode when closing the sheet
                if (!open) {
                    setIsEnhancedMode(false);
                    setIsTransitioning(false);
                }
            }
        }}>
            <SheetTrigger asChild>
                <Button 
                    variant="outline" 
                    size="icon"
                    className="fixed bottom-4 right-4 h-12 w-12 rounded-full bg-primary hover:bg-primary/90 guardian-button"
                >
                    <Bot className="h-6 w-6 text-white" />
                </Button>
            </SheetTrigger>
            <SheetContent 
                side="right" 
                className={cn(
                    "p-0 bg-[#0F1729] border-l border-[#1D2739] sm:max-w-[800px] !max-w-[800px] [&>button]:hidden overflow-hidden guardian-sheet-content guardian-normal-mode",
                    isEnhancedMode ? "!w-full !h-full !sm:max-w-full !max-w-full !inset-0 fixed guardian-enhanced-transition" : "w-[800px]",
                    isTransitioning && "guardian-enhanced-transition transitioning"
                )}
            >
                <div className="flex h-full" onDragEnter={handleDragEnter}>
                    {/* Full screen drop overlay */}
                    {isDragging && (
                        <div 
                            className="absolute inset-0 bg-black/60 flex items-center justify-center z-50"
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            <div className="p-10 rounded-xl bg-primary/10 border-2 border-dashed border-primary flex flex-col items-center">
                                <Image className="h-16 w-16 mb-4 text-primary" />
                                <span className="text-lg font-medium text-primary">Drop image to attach to your message</span>
                            </div>
                        </div>
                    )}
                    <div className="relative flex-none w-[260px] overflow-hidden">
                        <ChatHistorySlider 
                            isOpen={isHistoryOpen}
                            onClose={() => setIsHistoryOpen(false)}
                            histories={histories}
                            activeId={activeHistoryId}
                            onSelect={handleHistorySelect}
                            onNewChat={handleNewChat}
                        />
                    </div>
                    <div className={cn(
                        "flex flex-col flex-1 min-w-0 transition-[margin] duration-300 ease-in-out",
                        isHistoryOpen ? "ml-0" : "ml-[-260px]"
                    )}>
                        {/* Header */}
                        <div className="flex items-center justify-between h-12 px-4 border-b border-[#1D2739]">
                            <div className="flex items-center gap-3">
                                <div className="relative z-50">
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-7 w-7 hover:bg-[#1D2739] rounded-sm"
                                        onClick={() => setIsHistoryOpen(!isHistoryOpen)}
                                    >
                                        <Menu className="h-4 w-4 text-textmedium" />
                                    </Button>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Bot className="h-5 w-5 text-primary" />
                                    <span className="text-sm text-textlight">Guardian AI</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Enhanced mode toggle button */}
                                <Button
                                    variant="ghost"
                                    className="h-7 bg-primarytransparent hover:bg-primary/90 text-textmedium border border-primary text-sm font-normal rounded-sm px-3"
                                    onClick={toggleEnhancedMode}
                                    disabled={isTransitioning}
                                    title={isEnhancedMode ? "Exit Full Screen (⌘E / Ctrl+E)" : "Full Screen (⌘E / Ctrl+E)"}
                                >
                                    {isEnhancedMode ? "Exit Full Screen" : "Full Screen"}
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="h-7 bg-primarytransparent hover:bg-primary/90 text-textmedium border border-primary text-sm font-normal rounded-sm px-3"
                                    onClick={handleNewChat}
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    New Chat
                                </Button>
                            </div>
                        </div>

                        {/* Messages */}
                        <ScrollArea 
                            className="flex-1 bg-[#131B2E]"
                            onDragEnter={handleDragEnter}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                            ref={scrollAreaRef}
                        >
                            {messages.length === 0 ? (
                                <div className="flex flex-col items-center justify-center h-full space-y-6 mt-4">
                                    <AnimatedLogo />
                                    <div className="text-center space-y-2">
                                        <h2 className="text-2xl font-semibold text-textdark">Welcome to Guardian</h2>
                                        <p className="text-textmedium">Your intelligent observability assistant.</p>
                                        <p className="text-xs text-textdark mt-4">
                                            <span className="bg-[#1D2739] px-2 py-1 rounded mr-1">⌘G</span> or 
                                            <span className="bg-[#1D2739] px-2 py-1 rounded ml-1">Ctrl+G</span> 
                                            <span className="ml-2">to capture a screenshot</span>
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {messages.map((message, index) => (
                                        <ChatMessage 
                                            key={index}
                                            message={message}
                                            index={index}
                                            isEnhancedMode={isEnhancedMode}
                                            totalWindowWidth={totalWindowWidth}
                                            user={user}
                                            prevToolName={messages.find(msg => msg.tool_call_id === message.tool_call_response_id)?.tool_call_function_name}
                                        />
                                    ))}
                                    <div ref={messagesEndRef} />
                                </>
                            )}
                        </ScrollArea>

                        {/* Input */}
                        <ChatInput
                            input={input}
                            setInput={setInput}
                            isLoading={isLoading}
                            isStopping={isStopping}
                            handleStopGenerating={handleStopGenerating}
                            handleSubmit={handleSubmit}
                            handleScreenshotUpload={handleScreenshotUpload}
                            removeScreenshot={removeScreenshot}
                            screenshot={screenshot}
                            captureAndOpenChat={captureAndOpenChat}
                            isEnhancedMode={isEnhancedMode}
                            totalWindowWidth={totalWindowWidth * 1.02}
                            inputRef={inputRef}
                            handleDragEnter={handleDragEnter}
                            handleDragOver={handleDragOver}
                            handleDrop={handleDrop}
                        />
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
} 